import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import axiosInstance from '../../config/axiosApi/axiosApi';
import APIs from '../../Constants/apis';
import { login } from '../../app/auth/authSlice';
import Preloader from '../../components/CustomComponents/preLoad/preLoader';
import showErrorToast, { showSuccessToast } from '../../components/CustomComponents/toast/toast';
import userDataService from '../../services/userDataService';
import { saveUserToStorage, getCurrentUser } from '../../utils/userStorage';
import { getPostLoginPath } from '../../utils/authRedirect';
import {
  normalizeSubscriptionAccessFromApi,
  resolveSubscriptionAccess,
} from '../../utils/subscriptionAccess';

const AutoLogin = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    const processAutoLogin = async () => {
      try {
        const token = searchParams.get('token');
        const compId = searchParams.get('compId');
        const userName = searchParams.get('userName');
        const deviceId = searchParams.get('deviceId');

        if (!token) {
          setErrorMessage('Missing auto-login token');
          setLoading(false);
          setTimeout(() => {
            navigate('/user/login', { replace: true });
          }, 3000);
          return;
        }

        const payload = {
          Token: token
        };

        if (compId) payload.CompId = parseInt(compId, 10);
        if (userName) payload.UserName = userName;
        if (deviceId) payload.DeviceId = deviceId;

        const response = await axiosInstance.post(APIs.AUTO_LOGIN, payload);

        if (response.data.status && response.data.data) {
          const authToken = response.data.data.Token || response.data.data.token;
          const UserData = response.data.data.UserData || response.data.data.userData;
          const CompanyData = response.data.data.CompanyData || response.data.data.companyData;
          const IsAutoLogin = response.data.data.IsAutoLogin || response.data.data.isAutoLogin || true;
          const apiSubscription =
            response.data.data.subscriptionAccess ?? response.data.data.SubscriptionAccess;

          if (!authToken) {
            setErrorMessage('Authentication token missing from server response');
            showErrorToast('Auto-login failed: No authentication token');
            setLoading(false);
            setTimeout(() => navigate('/user/login', { replace: true }), 3000);
            return;
          }

          if (!UserData) {
            setErrorMessage('User data missing from server response');
            showErrorToast('Auto-login failed: Invalid user data');
            setLoading(false);
            setTimeout(() => navigate('/user/login', { replace: true }), 3000);
            return;
          }

          let subscriptionAccess = normalizeSubscriptionAccessFromApi(apiSubscription);
          const apiSubscriptionAccess = subscriptionAccess;

          const initialUserData = {
            token: authToken,
            userData: UserData,
            refreshToken: null,
            keepMeLogin: false,
            isAutoLogin: IsAutoLogin || false,
            subscriptionAccessFromApi: apiSubscriptionAccess,
          };

          dispatch(login(initialUserData));
          saveUserToStorage(initialUserData);
          localStorage.setItem("keepMeLogin", JSON.stringify(false));
          await new Promise(resolve => setTimeout(resolve, 300));

          let sessionUser = {
            userData: UserData,
            profileData: CompanyData || null,
          };

          try {
            const userDataResponse = await userDataService.fetchUserData();
            subscriptionAccess = resolveSubscriptionAccess(
              userDataResponse.profileData,
              apiSubscriptionAccess
            );
            const completeUserData = {
              token: authToken,
              userData: UserData,
              refreshToken: null,
              keepMeLogin: false,
              isAutoLogin: IsAutoLogin || false,
              profileData: userDataResponse.profileData,
              settingsData: userDataResponse.settingsData,
              lastDataRefresh: userDataResponse.lastDataRefresh,
              subscriptionAccessFromApi: apiSubscriptionAccess,
              subscriptionAccess,
            };
            sessionUser = completeUserData;
            dispatch(login(completeUserData));
          } catch (error) {
            const fallbackUserData = {
              token: authToken,
              userData: UserData,
              refreshToken: null,
              keepMeLogin: false,
              isAutoLogin: IsAutoLogin || false,
              profileData: CompanyData || null,
              settingsData: null,
              lastDataRefresh: new Date().toISOString(),
              subscriptionAccessFromApi: apiSubscriptionAccess,
              subscriptionAccess: resolveSubscriptionAccess(CompanyData, apiSubscriptionAccess),
            };
            sessionUser = fallbackUserData;
            dispatch(login(fallbackUserData));
            subscriptionAccess = fallbackUserData.subscriptionAccess;
          }

          showSuccessToast(`Welcome to ${CompanyData?.compName || 'BackOffice'}! (Auto-Login)`);

          const redirectTo = getPostLoginPath({ subscriptionAccess, user: sessionUser });
          setTimeout(() => {
            navigate(redirectTo, { replace: true });
          }, 1500);
        } else {
          setErrorMessage(response.data.message || 'Auto-login failed');
          showErrorToast(response.data.message || 'Auto-login failed');
          setLoading(false);
          setTimeout(() => {
            navigate('/user/login', { replace: true });
          }, 3000);
        }
      } catch (error) {
        const errorMsg = error.response?.data?.message || 'Auto-login failed. Please try again.';
        setErrorMessage(errorMsg);
        showErrorToast(errorMsg);
        setLoading(false);
        
        setTimeout(() => {
          navigate('/user/login', { replace: true });
        }, 3000);
      }
    };

    processAutoLogin();
  }, [searchParams, navigate, dispatch]);

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      height: '100vh',
      backgroundColor: '#f8f9fa'
    }}>
      {loading ? (
        <>
          <Preloader />
          <div style={{
            marginTop: '20px',
            fontSize: '18px',
            color: '#333',
            textAlign: 'center'
          }}>
            <i className="fa fa-lock fa-3x mb-3" style={{ color: '#00acac' }}></i>
            <h3>Auto-Login in Progress...</h3>
            <p className="text-muted">Please wait while we securely log you in</p>
          </div>
        </>
      ) : (
        <div style={{
          textAlign: 'center',
          padding: '40px',
          backgroundColor: 'white',
          borderRadius: '12px',
          boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
          maxWidth: '500px'
        }}>
          <i className="fa fa-exclamation-circle fa-3x mb-3" style={{ color: '#dc3545' }}></i>
          <h3 style={{ color: '#dc3545', marginBottom: '20px' }}>Auto-Login Failed</h3>
          <p style={{ fontSize: '16px', color: '#666', marginBottom: '20px' }}>
            {errorMessage}
          </p>
          <p className="text-muted">Redirecting to login page...</p>
        </div>
      )}
    </div>
  );
};

export default AutoLogin;

